
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { Plus, Edit, Trash2 } from "lucide-react";
import { AircraftTypeForm } from "../admin/AircraftTypeForm";
import { getAircraftTypes } from "@/services/aircraftService";
import { supabase } from "@/integrations/supabase/client";
import { AircraftType } from "@/types/flight";

export function OrganizationAircraftManagement() {
  const [aircraftTypes, setAircraftTypes] = useState<AircraftType[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedAircraftType, setSelectedAircraftType] = useState<AircraftType | null>(null);

  const fetchAircraftTypes = async () => {
    try {
      const data = await getAircraftTypes();
      setAircraftTypes(data);
    } catch (error) {
      console.error('Error fetching aircraft types:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch aircraft types",
      });
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchAircraftTypes();
      setLoading(false);
    };
    loadData();
  }, []);

  const handleCreateSuccess = () => {
    setIsCreateDialogOpen(false);
    fetchAircraftTypes();
  };

  const handleEditSuccess = () => {
    setSelectedAircraftType(null);
    fetchAircraftTypes();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this aircraft type?")) return;

    try {
      const { error } = await supabase
        .from('aircraft_types')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Aircraft type deleted",
        description: "The aircraft type has been deleted successfully.",
      });
      fetchAircraftTypes();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete aircraft type",
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader style={{ backgroundColor: 'orange' }}>
          <CardTitle className="flex items-center gap-2">
            <img src="/NAC_Icon_Only.png" alt="NAC Logo" className="h-5 w-5" />
            Aircraft Type Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">Loading aircraft types...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader style={{ backgroundColor: 'orange' }}>
        <CardTitle className="flex items-center gap-2">
          <img src="/NAC_Icon_Only.png" alt="NAC Logo" className="h-5 w-5" />
          Aircraft Type Management
        </CardTitle>
        <CardDescription>
          Manage aircraft types for your organization's flights
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-muted-foreground">
            {aircraftTypes.length} aircraft types total
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Aircraft Type
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Aircraft Type</DialogTitle>
              </DialogHeader>
              <AircraftTypeForm onSubmit={handleCreateSuccess} />
            </DialogContent>
          </Dialog>
        </div>

        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {aircraftTypes.map((aircraftType) => (
                <TableRow key={aircraftType.id}>
                  <TableCell className="font-medium">{aircraftType.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{aircraftType.code}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Dialog 
                        open={selectedAircraftType?.id === aircraftType.id} 
                        onOpenChange={(open) => setSelectedAircraftType(open ? aircraftType : null)}
                      >
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Edit Aircraft Type</DialogTitle>
                          </DialogHeader>
                          <AircraftTypeForm 
                            aircraftType={selectedAircraftType}
                            onSubmit={handleEditSuccess}
                          />
                        </DialogContent>
                      </Dialog>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleDelete(aircraftType.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
